/**
 * @module Events
 */
import { publicConstants } from '../constants';

/**
 * Provides APIs for subscribing to events like CaseEvents using PubSubUtils.
 */
class Events {
  /**
   * Obtains the case related events that can be subscribed to using the getPubSubUtils() API.
   * @function getCaseEvent
   * @public
   * @returns The name of the event as a string.
   * @example <caption>In this example, the API returns the name of the case related event that can be subscribed to.</caption>
   * const CaseEvent = PCore.getEvents().getCaseEvent().CASE_CREATED;
   */
  static getCaseEvent = () => {
    return Object.freeze(publicConstants.PUB_SUB_EVENTS.CASE_EVENTS);
  };

  /**
   * Obtains the data object related events that can be subscribed to using the getPubSubUtils() API.
   * @function getDataEvent
   * @public
   * @returns The name of the event as a string.
   * @example <caption>In this example, the API returns the name of the data object related event that can be subscribed to.</caption>
   * const dataEvent = PCore.getEvents().getDataEvent().DATA_OBJECT_CREATED;
   */
  static getDataEvent = () => {
    return Object.freeze(publicConstants.PUB_SUB_EVENTS.DATA_EVENTS);
  };

  /**
   * This function helps to get all the transient items related events
   * which are supposed to be used to subscribe using getPubSubUtils.
   * @function getTransientEvent
   * @returns Event Name which needs to be subscribed to.
   * @example <caption>In these examples the API returns the transient event name which can be used to subscribe.</caption>
   * const TransientEvent = PCore.getEvents().getTransientEvent().UPDATE_PROMOTED_FILTERS;
   * // console output:
   * // updatePromotedFilters
   * @private
   */
  static getTransientEvent = () => {
    return Object.freeze(publicConstants.PUB_SUB_EVENTS.EVENT_TRANSIENT_ITEMS);
  };

  /**
   * Obtains the bootstrap-shell related events that can be subscribed to using the getPubSubUtils() API.
   * @function getBootstrapShellEvent
   * @returns All the bootstrap-shell event name.
   * @example <caption>In this example, the API returns the name of the bootstrap-shell related event that can be subscribed to.</caption>
   * const bootstrapShellEvent = PCore.getEvents().getBootstrapShellEvent().CONSTELLATION_LOADED;
   * @private
   */
  static getBootstrapShellEvent = () => Object.freeze(publicConstants.PUB_SUB_EVENTS.BOOTSTRAP_SHELL_EVENTS);

  /**
   * Obtains the event "EVENT_BULKACTION" when the bulk action is submitted
   * @function getBulkProcessEvent
   * @public
   * @returns The name of the event as a string.
   */
  static getBulkProcessEvent = () => {
    return Object.freeze(publicConstants.PUB_SUB_EVENTS.EVENT_BULKACTION);
  };

  /**
   * Obtains the event "EVENT_LOCALACTION_SUBMISSION" when a local action is submitted
   * @function getLocalActionProcessEvent
   * @public
   * @returns The name of the event as a string.
   */
  static getLocalActionProcessEvent = () => {
    return Object.freeze(publicConstants.PUB_SUB_EVENTS.EVENT_LOCALACTION_SUBMISSION);
  };
}
export default Events;
